import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import type { Referidor, Referido, SyncQueueItem, UserSettings } from '@/types';

// Database schema definition
interface PDRDatabase extends DBSchema {
  referidores: {
    key: string;
    value: Referidor;
    indexes: {
      'by-fecha': string;
      'by-vendedor': string;
      'by-sync': number;
    };
  };
  referidos: {
    key: string;
    value: Referido;
    indexes: {
      'by-referidor': string;
      'by-telefono': string;
      'by-vendedor': string;
      'by-sync': number;
    };
  };
  syncQueue: {
    key: string;
    value: SyncQueueItem;
    indexes: {
      'by-timestamp': number;
      'by-status': string;
    };
  };
  settings: {
    key: string;
    value: UserSettings;
  };
  cities: {
    key: string;
    value: {
      name: string;
      isCustom: boolean;
      usageCount: number;
      lastUsed: string;
    };
  };
}

const DB_NAME = 'pdr-referidos-db';
const DB_VERSION = 1;

class IndexedDBService {
  private dbPromise: Promise<IDBPDatabase<PDRDatabase>>;

  constructor() {
    this.dbPromise = this.initDB();
  }

  private async initDB(): Promise<IDBPDatabase<PDRDatabase>> {
    return openDB<PDRDatabase>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Referidores store
        if (!db.objectStoreNames.contains('referidores')) {
          const referidorStore = db.createObjectStore('referidores', { keyPath: 'id' });
          referidorStore.createIndex('by-fecha', 'fecha_registro');
          referidorStore.createIndex('by-vendedor', 'vendedor_id');
          referidorStore.createIndex('by-sync', 'synced');
        }

        // Referidos store
        if (!db.objectStoreNames.contains('referidos')) {
          const referidoStore = db.createObjectStore('referidos', { keyPath: 'id' });
          referidoStore.createIndex('by-referidor', 'referidor_id');
          referidoStore.createIndex('by-telefono', 'telefono');
          referidoStore.createIndex('by-vendedor', 'vendedor_id');
          referidoStore.createIndex('by-sync', 'synced');
        }

        // Sync queue store
        if (!db.objectStoreNames.contains('syncQueue')) {
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id' });
          syncStore.createIndex('by-timestamp', 'timestamp');
          syncStore.createIndex('by-status', 'status');
        }

        // Settings store
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', { keyPath: 'vendorId' });
        }

        // Cities store
        if (!db.objectStoreNames.contains('cities')) {
          db.createObjectStore('cities', { keyPath: 'name' });
        }
      },
    });
  }

  // ========================================
  // Referidor Operations
  // ========================================

  async addReferidor(referidor: Referidor): Promise<string> {
    const db = await this.dbPromise;
    await db.add('referidores', referidor);
    return referidor.id;
  }

  async getReferidor(id: string): Promise<Referidor | undefined> {
    const db = await this.dbPromise;
    return db.get('referidores', id);
  }

  async getAllReferidores(vendedorId?: string): Promise<Referidor[]> {
    const db = await this.dbPromise;
    if (vendedorId) {
      return db.getAllFromIndex('referidores', 'by-vendedor', vendedorId);
    }
    return db.getAll('referidores');
  }

  async getUnsyncedReferidores(): Promise<Referidor[]> {
    const db = await this.dbPromise;
    const all = await db.getAll('referidores');
    return all.filter((r) => !r.synced);
  }

  async updateReferidor(referidor: Referidor): Promise<void> {
    const db = await this.dbPromise;
    await db.put('referidores', referidor);
  }

  async markReferidorSynced(id: string): Promise<void> {
    const db = await this.dbPromise;
    const referidor = await db.get('referidores', id);
    if (referidor) {
      referidor.synced = true;
      referidor.syncedAt = new Date().toISOString();
      await db.put('referidores', referidor);
    }
  }

  // ========================================
  // Referido Operations
  // ========================================

  async addReferido(referido: Referido): Promise<string> {
    const db = await this.dbPromise;
    await db.add('referidos', referido);
    return referido.id;
  }

  async getReferido(id: string): Promise<Referido | undefined> {
    const db = await this.dbPromise;
    return db.get('referidos', id);
  }

  async getReferidosByReferidor(referidorId: string): Promise<Referido[]> {
    const db = await this.dbPromise;
    return db.getAllFromIndex('referidos', 'by-referidor', referidorId);
  }

  async getAllReferidos(vendedorId?: string): Promise<Referido[]> {
    const db = await this.dbPromise;
    if (vendedorId) {
      return db.getAllFromIndex('referidos', 'by-vendedor', vendedorId);
    }
    return db.getAll('referidos');
  }

  async getUnsyncedReferidos(): Promise<Referido[]> {
    const db = await this.dbPromise;
    const all = await db.getAll('referidos');
    return all.filter((r) => !r.synced);
  }

  async updateReferido(referido: Referido): Promise<void> {
    const db = await this.dbPromise;
    await db.put('referidos', referido);
  }

  async markReferidoSynced(id: string): Promise<void> {
    const db = await this.dbPromise;
    const referido = await db.get('referidos', id);
    if (referido) {
      referido.synced = true;
      referido.syncedAt = new Date().toISOString();
      await db.put('referidos', referido);
    }
  }

  async markReferidosSynced(ids: string[]): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction('referidos', 'readwrite');
    const now = new Date().toISOString();

    await Promise.all(
      ids.map(async (id) => {
        const referido = await tx.store.get(id);
        if (referido) {
          referido.synced = true;
          referido.syncedAt = now;
          await tx.store.put(referido);
        }
      })
    );

    await tx.done;
  }

  /**
   * Check if a phone number already exists for this vendor
   */
  async checkDuplicatePhone(telefono: string, vendedorId: string): Promise<Referido | null> {
    const db = await this.dbPromise;
    const referidos = await db.getAllFromIndex('referidos', 'by-vendedor', vendedorId);

    const normalizedPhone = this.normalizePhone(telefono);
    const duplicate = referidos.find(
      (r) => this.normalizePhone(r.telefono) === normalizedPhone
    );

    return duplicate || null;
  }

  private normalizePhone(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  // ========================================
  // Sync Queue Operations
  // ========================================

  async addToSyncQueue(item: SyncQueueItem): Promise<void> {
    const db = await this.dbPromise;
    await db.add('syncQueue', item);
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    const db = await this.dbPromise;
    return db.getAllFromIndex('syncQueue', 'by-timestamp');
  }

  async getPendingSyncItems(): Promise<SyncQueueItem[]> {
    const db = await this.dbPromise;
    return db.getAllFromIndex('syncQueue', 'by-status', 'pending');
  }

  async updateSyncItem(item: SyncQueueItem): Promise<void> {
    const db = await this.dbPromise;
    await db.put('syncQueue', item);
  }

  async removeSyncItem(id: string): Promise<void> {
    const db = await this.dbPromise;
    await db.delete('syncQueue', id);
  }

  async clearCompletedSyncItems(): Promise<void> {
    const db = await this.dbPromise;
    const items = await db.getAllFromIndex('syncQueue', 'by-status', 'completed');
    const tx = db.transaction('syncQueue', 'readwrite');
    await Promise.all(items.map((item) => tx.store.delete(item.id)));
    await tx.done;
  }

  // ========================================
  // Settings Operations
  // ========================================

  async getSettings(vendorId: string): Promise<UserSettings | undefined> {
    const db = await this.dbPromise;
    return db.get('settings', vendorId);
  }

  async saveSettings(settings: UserSettings): Promise<void> {
    const db = await this.dbPromise;
    await db.put('settings', settings);
  }

  // ========================================
  // Cities Operations
  // ========================================

  async getCustomCities(): Promise<string[]> {
    const db = await this.dbPromise;
    const cities = await db.getAll('cities');
    return cities.filter((c) => c.isCustom).map((c) => c.name);
  }

  async addCustomCity(name: string): Promise<void> {
    const db = await this.dbPromise;
    await db.put('cities', {
      name,
      isCustom: true,
      usageCount: 1,
      lastUsed: new Date().toISOString(),
    });
  }

  async incrementCityUsage(name: string): Promise<void> {
    const db = await this.dbPromise;
    const city = await db.get('cities', name);
    if (city) {
      city.usageCount++;
      city.lastUsed = new Date().toISOString();
      await db.put('cities', city);
    }
  }

  // ========================================
  // Stats Operations
  // ========================================

  async getTodayStats(vendedorId: string): Promise<{ referidores: number; referidos: number }> {
    const today = new Date().toISOString().split('T')[0];
    const referidores = await this.getAllReferidores(vendedorId);
    const referidos = await this.getAllReferidos(vendedorId);

    const todayReferidores = referidores.filter(
      (r) => r.fecha_registro.startsWith(today)
    ).length;
    const todayReferidos = referidos.filter(
      (r) => r.createdAt.startsWith(today)
    ).length;

    return { referidores: todayReferidores, referidos: todayReferidos };
  }

  async getWeekStats(vendedorId: string): Promise<{ total: number; average: number }> {
    const referidos = await this.getAllReferidos(vendedorId);
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const weekReferidos = referidos.filter(
      (r) => new Date(r.createdAt) >= oneWeekAgo
    );

    return {
      total: weekReferidos.length,
      average: Math.round(weekReferidos.length / 7),
    };
  }

  async getMonthStats(vendedorId: string): Promise<{ total: number }> {
    const referidos = await this.getAllReferidos(vendedorId);
    const firstOfMonth = new Date();
    firstOfMonth.setDate(1);
    firstOfMonth.setHours(0, 0, 0, 0);

    const monthReferidos = referidos.filter(
      (r) => new Date(r.createdAt) >= firstOfMonth
    );

    return { total: monthReferidos.length };
  }

  async getPendingSyncCount(): Promise<number> {
    const db = await this.dbPromise;
    const pending = await db.getAllFromIndex('syncQueue', 'by-status', 'pending');
    return pending.length;
  }
}

export const indexedDBService = new IndexedDBService();
